import styled from 'styled-components'

import { DataGridTheme, LeftRight } from './DataGrid.types'

export const rowHoverStyle = (
  rowIndexes: number[],
  theme: DataGridTheme,
  handlesClicks: boolean,
) => {
  return rowIndexes
    .map(
      rowIndex =>
        `
  .row-${rowIndex} {
    ${handlesClicks ? 'cursor: pointer;' : ''}
    background-color: ${theme.selectedRowBackground};
  }
`,
    )
    .join(' ')
}

export interface BaseCellProps {
  $isFirstColumn?: boolean
  $isHeaderCell?: boolean
  $isSticky?: boolean
  $leftVar?: string
  $noBottomBorder?: boolean
  $noRightBorder?: boolean
  $rightVar?: string
  $withLeftBorder?: boolean
  $withRightBorder?: boolean
  $contentAlign?: string
}

export function rgba(color: string, alpha: number) {
  if (alpha < 0 || alpha > 1) {
    throw new Error('Alpha must be between 0 and 1')
  }
  return (
    color +
    Math.round(alpha * 255)
      .toString(16)
      .padStart(2, '0')
  )
}

const getSideBorder = (theme: DataGridTheme, side: LeftRight, specialColumnBorder?: boolean) => {
  // we render theme borderWidth only on the right & bottom cell edges to avoid double borders
  if (
    !(side === LeftRight.Right && (theme.borderWidth || theme.borderWidthVertical)) &&
    !specialColumnBorder
  ) {
    return
  }
  const width = Math.max(1, theme.borderWidth || theme.borderWidthVertical)
  return `border-${side}: ${width}px solid ${
    specialColumnBorder ? theme.borderColorGroupsAndPinned : theme.borderColor
  }`
}

const getBorderBottom = (theme: DataGridTheme, $isHeaderCell?: boolean) => {
  if (
    !theme.borderWidth &&
    !theme.borderWidthHorizontal &&
    !($isHeaderCell && theme.header.bottomBorderWidth)
  ) {
    return
  }
  return `border-bottom: ${
    theme.borderWidth ||
    theme.borderWidthHorizontal ||
    ($isHeaderCell && theme.header.bottomBorderWidth)
  }px solid ${theme.borderColor}`
}

export const BaseCell = styled.div<BaseCellProps>`
  ${({ $isSticky }) => ($isSticky ? `position: sticky; z-index: 1;` : 'position: relative;')}
  ${({ $leftVar, $isSticky }) => $isSticky && $leftVar};
  ${({ $rightVar, $isSticky }) => $isSticky && $rightVar};

  display: flex;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSize}px;
  ${({ $contentAlign }) => $contentAlign && `justify-content: ${$contentAlign}`};
  ${({ $contentAlign }) => $contentAlign && `text-align: ${$contentAlign}`};

  padding: 4px 4px 4px
    ${({ $isFirstColumn, theme }) => ($isFirstColumn ? theme.firstColumnPadding : 4)}px;

  ${({
    $isHeaderCell,
    $noBottomBorder,
    $noRightBorder,
    $withLeftBorder,
    $withRightBorder,
    theme,
  }) =>
    (theme.borderWidth > 0 ||
      theme.borderWidthVertical > 0 ||
      theme.borderWidthHorizontal > 0 ||
      $withRightBorder ||
      $withLeftBorder) &&
    `
      &:after {
        pointer-events: none;
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        content: '';
        ${getSideBorder(theme, LeftRight.Left, $withLeftBorder)};
        ${$noRightBorder ? '' : getSideBorder(theme, LeftRight.Right, $withRightBorder)};
        ${$noBottomBorder ? '' : getBorderBottom(theme, $isHeaderCell)};
      }
  `}
`

export const ScrollWrapper = styled.div`
  overscroll-behavior: none;
  overflow: auto;
  width: 100%;
  height: 100%;
  border: ${p =>
    p.theme.outerBorderWidth ? `${p.theme.outerBorderWidth}px solid ${p.theme.borderColor}` : '0'};
`

export const Root = styled.div<{ $gridColTemplate: string }>`
  display: grid;
  min-width: min-content; // fixes the issue with sticky columns being pushed left for small grid area
  grid-template-columns: ${({ $gridColTemplate }) => $gridColTemplate};
`
